import React, { useState, useRef } from 'react';
import { Download, File, CheckCircle, Loader2, AlertCircle } from 'lucide-react';

const FileCompressor = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [compressionMode, setCompressionMode] = useState('target'); // 'target' or 'quality'
  const [targetSize, setTargetSize] = useState(150);
  const [targetUnit, setTargetUnit] = useState('KB');
  const [quality, setQuality] = useState(0.8);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressedFile, setCompressedFile] = useState(null);
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Convert size to bytes
  const convertToBytes = (size, unit) => {
    const multipliers = {
      'KB': 1024,
      'MB': 1024 * 1024,
      'GB': 1024 * 1024 * 1024
    };
    return size * (multipliers[unit] || 1024);
  };

  // Format bytes to human readable
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Compress image using Canvas API with advanced iterative compression
  const compressImage = async (file, targetBytes) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate optimal dimensions to achieve target size
          // Start with aggressive resizing if needed
          let width = img.width;
          let height = img.height;
          const originalSize = width * height;
          
          // Estimate required dimensions based on target size
          // JPEG compression ratio is roughly 10:1 to 20:1 for photos
          const estimatedPixels = targetBytes * 15; // Conservative estimate
          const scaleFactor = Math.sqrt(estimatedPixels / originalSize);
          
          // Apply scaling if needed (but don't go below 400px to maintain quality)
          if (scaleFactor < 1) {
            width = Math.max(400, Math.round(width * scaleFactor));
            height = Math.max(400, Math.round(height * scaleFactor));
          }
          
          // Also cap maximum dimension
          const maxDimension = 2000;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }
          
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = width;
          canvas.height = height;
          
          // Use high-quality image rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Draw image
          ctx.drawImage(img, 0, 0, width, height);
          
          // Advanced binary search for quality
          let bestBlob = null;
          let bestDiff = Infinity;
          let currentWidth = width;
          let currentHeight = height;
          
          const compressWithQuality = (minQuality, maxQuality, iterations = 0) => {
            if (iterations > 20) {
              // Use best result found or create a final low-quality version
              if (bestBlob) {
                resolve(bestBlob);
              } else {
                canvas.toBlob((blob) => {
                  if (blob) {
                    resolve(blob);
                  } else {
                    reject(new Error('Compression failed'));
                  }
                }, 'image/jpeg', 0.1);
              }
              return;
            }
            
            const currentQuality = (minQuality + maxQuality) / 2;
            
            canvas.toBlob((blob) => {
              if (!blob) {
                // If blob creation fails, try lower quality
                if (currentQuality > 0.1) {
                  compressWithQuality(0.1, currentQuality - 0.05, iterations + 1);
                } else {
                  // Last resort - use best blob found
                  if (bestBlob) {
                    resolve(bestBlob);
                  } else {
                    reject(new Error('Compression failed - unable to create blob'));
                  }
                }
                return;
              }
              
              const blobSize = blob.size;
              const diff = Math.abs(blobSize - targetBytes);
              
              // Track best result
              if (diff < bestDiff) {
                bestDiff = diff;
                bestBlob = blob;
              }
              
              // Check if we're close enough (within 5% of target)
              const tolerance = targetBytes * 0.05;
              if (diff <= tolerance || iterations > 18) {
                resolve(blob);
                return;
              }
              
              // Binary search
              if (blobSize > targetBytes) {
                // Need more compression - reduce quality or dimensions
                if (currentQuality <= 0.15 && currentWidth > 400) {
                  // If quality is already low, try reducing dimensions
                  currentWidth = Math.max(400, Math.round(currentWidth * 0.9));
                  currentHeight = Math.max(400, Math.round(currentHeight * 0.9));
                  canvas.width = currentWidth;
                  canvas.height = currentHeight;
                  ctx.drawImage(img, 0, 0, currentWidth, currentHeight);
                  compressWithQuality(0.1, 0.95, iterations + 1);
                } else {
                  compressWithQuality(0.1, currentQuality, iterations + 1);
                }
              } else {
                // Can use better quality
                compressWithQuality(currentQuality, maxQuality, iterations + 1);
              }
            }, 'image/jpeg', currentQuality);
          };
          
          // Start compression with wider range
          compressWithQuality(0.1, 0.95);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Compress PDF using PDF.js (if available) or return as-is
  const compressPDF = async (file, targetBytes) => {
    // For PDF, we'll use a simpler approach - return original if already small enough
    if (file.size <= targetBytes) {
      return file;
    }
    
    // Note: Full PDF compression requires backend or specialized library
    // For now, we'll return a note that PDF compression needs backend
    throw new Error('PDF compression requires backend processing. Please use image files for client-side compression.');
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Please select an image (JPG, PNG, WebP) or PDF file');
      return;
    }
    
    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }
    
    setSelectedFile(file);
    setOriginalSize(file.size);
    setCompressedFile(null);
    setError('');
  };

  // Handle compression
  const handleCompress = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }
    
    setIsCompressing(true);
    setError('');
    
    try {
      let targetBytes;
      if (compressionMode === 'target') {
        targetBytes = convertToBytes(targetSize, targetUnit);
      } else {
        // For quality mode, estimate target size
        targetBytes = selectedFile.size * quality;
      }
      
      let compressedBlob;
      
      if (selectedFile.type.startsWith('image/')) {
        compressedBlob = await compressImage(selectedFile, targetBytes);
      } else if (selectedFile.type === 'application/pdf') {
        compressedBlob = await compressPDF(selectedFile, targetBytes);
      } else {
        throw new Error('Unsupported file type');
      }
      
      // Create a File object from blob
      const compressedFileObj = new File(
        [compressedBlob],
        `compressed_${selectedFile.name}`,
        { type: selectedFile.type.startsWith('image/') ? 'image/jpeg' : selectedFile.type }
      );
      
      setCompressedFile(compressedFileObj);
      setCompressedSize(compressedBlob.size);
      
      // Save to localStorage
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileData = {
          name: compressedFileObj.name,
          type: compressedFileObj.type,
          size: compressedBlob.size,
          originalName: selectedFile.name,
          originalSize: selectedFile.size,
          data: e.target.result, // Base64 encoded
          timestamp: new Date().toISOString()
        };
        
        // Get existing compressed files from localStorage
        const existingFiles = JSON.parse(localStorage.getItem('compressedFiles') || '[]');
        existingFiles.push(fileData);
        
        // Keep only last 10 files to avoid localStorage limit
        if (existingFiles.length > 10) {
          existingFiles.shift();
        }
        
        localStorage.setItem('compressedFiles', JSON.stringify(existingFiles));
        localStorage.setItem(`compressedFile_${Date.now()}`, JSON.stringify(fileData));
        
        console.log('File saved to localStorage:', fileData.name);
      };
      reader.readAsDataURL(compressedBlob);
      
    } catch (err) {
      setError(err.message || 'Compression failed. Please try again.');
      console.error('Compression error:', err);
    } finally {
      setIsCompressing(false);
    }
  };

  // Handle download from localStorage
  const handleDownload = () => {
    if (!compressedFile) return;
    
    // Get the latest compressed file from localStorage
    const existingFiles = JSON.parse(localStorage.getItem('compressedFiles') || '[]');
    if (existingFiles.length === 0) {
      setError('No compressed file found in localStorage');
      return;
    }
    
    const latestFile = existingFiles[existingFiles.length - 1];
    
    // Convert base64 to blob
    const byteCharacters = atob(latestFile.data.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: latestFile.type });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = latestFile.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Handle process another file
  const handleReset = () => {
    setSelectedFile(null);
    setCompressedFile(null);
    setOriginalSize(0);
    setCompressedSize(0);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            File <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Compressor</span>
          </h1>
          <p className="text-gray-600">Compress your files to target size and save to localStorage</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-6">
          {/* File Selection */}
          {!selectedFile && (
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select File
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-input"
                />
                <label
                  htmlFor="file-input"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <File className="w-12 h-12 text-gray-400 mb-2" />
                  <span className="text-gray-600">Click to select file</span>
                  <span className="text-sm text-gray-400 mt-1">JPG, PNG, WebP, or PDF (Max 50MB)</span>
                </label>
              </div>
            </div>
          )}

          {/* File Info */}
          {selectedFile && !compressedFile && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-600">Size: {formatBytes(selectedFile.size)}</p>
                </div>
                <button
                  onClick={handleReset}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Change File
                </button>
              </div>
            </div>
          )}

          {/* Compression Settings */}
          {selectedFile && !compressedFile && (
            <div className="mb-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Compression Mode
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="target"
                      checked={compressionMode === 'target'}
                      onChange={(e) => setCompressionMode(e.target.value)}
                      className="mr-2"
                    />
                    <span>Less than (e.g. 200 KB)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="quality"
                      checked={compressionMode === 'quality'}
                      onChange={(e) => setCompressionMode(e.target.value)}
                      className="mr-2"
                    />
                    <span>Quality Based</span>
                  </label>
                </div>
              </div>

              {compressionMode === 'target' && (
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Target Size
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={targetSize}
                        onChange={(e) => setTargetSize(Number(e.target.value))}
                        min="1"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <select
                        value={targetUnit}
                        onChange={(e) => setTargetUnit(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="KB">KB</option>
                        <option value="MB">MB</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {compressionMode === 'quality' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Quality: {Math.round(quality * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.05"
                    value={quality}
                    onChange={(e) => setQuality(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              )}

              <button
                onClick={handleCompress}
                disabled={isCompressing}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCompressing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Compressing...
                  </>
                ) : (
                  'Compress'
                )}
              </button>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Compression Result */}
          {compressedFile && (
            <div className="text-center">
              <div className="mb-6">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Compression Complete!</h3>
                <div className="text-gray-600 space-y-1">
                  <p>Original size: <span className="font-semibold">{formatBytes(originalSize)}</span></p>
                  <p className="text-lg">
                    → Compressed size: <span className="font-bold text-green-600">{formatBytes(compressedSize)}</span>
                  </p>
                  <p className="text-sm text-gray-500">
                    Reduction: {Math.round(((originalSize - compressedSize) / originalSize) * 100)}%
                  </p>
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleDownload}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Download File
                </button>
                <button
                  onClick={handleReset}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-lg transition-colors"
                >
                  Process Another File
                </button>
              </div>

              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700">
                  ✓ File saved to localStorage. You can access it later from browser storage.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Info Card */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-2">How it works:</h3>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li>Select an image file (JPG, PNG, WebP) or PDF</li>
            <li>Choose compression mode: Target size or Quality based</li>
            <li>Click Compress - uses advanced iterative compression algorithm</li>
            <li>Compressed file is automatically saved to localStorage</li>
            <li>Download button retrieves file from localStorage</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FileCompressor;

